import React, { useEffect } from "react"
import { Navigate, useNavigate, useOutletContext, useParams } from "react-router-dom"
import { createPortal } from "react-dom"
import ProjectSessionDetail from "../../../components/DashboardComponents/ProjectSessionDetail"
import {
  ExitSessionModal,
  SessionCompletedModal,
} from "../../../components/DashboardComponents/ProjectSessions"
import { useSessionDetailPlayer } from "../../../components/FloatingPlayer"
import useSessionDetail from "../../../hooks/useSessionDetail"
import { toSessionCityPayload } from "../../../helpers/session"
import { cityHubTabPath } from "../cityHubTabs"

/**
 * Inline shadow session detail inside City Hub when another session is
 * already open in the floating player — keeps the active widget visible.
 */
const EmbeddedCityShadowEventDetail = ({ sessionId, city, returnTo }) => {
  const navigate = useNavigate()
  const detail = useSessionDetail(sessionId, { city })

  const listingPath =
    returnTo || cityHubTabPath("shadow-working-events", city?.id) || "/city-hub"

  const goBack = () => navigate(listingPath)

  const handleCompletedClose = () => {
    detail.setIsCompletedOpen(false)
    navigate(listingPath, { replace: true })
  }

  const handleLeaveSession = async leavePayload => {
    await detail.onLeaveSession(leavePayload)
  }

  const cityForModal =
    city ||
    detail.session?.city ||
    (detail.session?.cityName ? { name: detail.session.cityName } : null)

  const sessionModals =
    typeof document === "undefined"
      ? null
      : createPortal(
          <>
            <ExitSessionModal
              isOpen={detail.isExitOpen}
              onClose={() => {
                if (!detail.isLeaving) detail.setIsExitOpen(false)
              }}
              city={cityForModal}
              onFinish={handleLeaveSession}
              isSubmitting={detail.isLeaving}
            />
            <SessionCompletedModal
              isOpen={detail.isCompletedOpen}
              onClose={handleCompletedClose}
              time={detail.completedStats.time}
              xp={detail.completedStats.xp}
              redirectHint="Returning to Shadow Working Events..."
            />
          </>,
          document.body,
        )

  return (
    <>
      <ProjectSessionDetail
        session={detail.session}
        timer={detail.displayTimer}
        isLoading={detail.isLoading}
        error={detail.error}
        members={detail.members}
        gallery={detail.gallery}
        membersTotal={detail.membersTotal}
        membersPage={detail.membersPage}
        membersPageSize={detail.membersPageSize}
        isMembersLoading={detail.isMembersLoading}
        membersError={detail.membersError}
        onMembersPageChange={detail.onMembersPageChange}
        searchQuery={detail.searchQuery}
        onSearchChange={detail.onSearchChange}
        statusFilters={detail.statusFilters}
        onStatusFiltersChange={detail.onStatusFiltersChange}
        sortBy={detail.sortBy}
        onSortByChange={detail.onSortByChange}
        sortDirection={detail.sortDirection}
        onSortDirectionChange={detail.onSortDirectionChange}
        onExit={detail.onExit}
        onTimerToggle={detail.onTimerToggle}
        isTimerUpdating={detail.isTimerUpdating}
        onClose={goBack}
        alwaysShowClose
        cityId={city?.id || null}
        currentUserId={detail.currentUserId}
        existingCollaboratorUserIds={detail.existingCollaboratorUserIds}
        onAddCollaborators={detail.onAddCollaborators}
        onRemoveCollaborator={detail.onRemoveCollaborator}
        isMutatingCollaborators={detail.isMutatingCollaborators}
        impressions={detail.impressions}
        reactingMemberId={detail.reactingMemberId}
        onToggleImpression={detail.onToggleImpression}
        timerAboveMeta
      />
      {sessionModals}
    </>
  )
}

/**
 * Shadow Working Events detail tab — nested under City Hub layout.
 *
 * If the floating widget is already showing a different in-progress
 * session, render this session inline in the tab outlet so the widget
 * stays put. Otherwise open the global player and return to the listing.
 */
const CityHubShadowEventDetailTab = () => {
  const { eventId } = useParams()
  const { city, tab } = useOutletContext()
  const { openSessionDetail, isOpen, sessionId: floatingSessionId } =
    useSessionDetailPlayer()

  const listingPath = cityHubTabPath("shadow-working-events", city?.id)

  const isOtherSession = Boolean(
    isOpen &&
      floatingSessionId &&
      eventId &&
      String(eventId) !== String(floatingSessionId),
  )

  useEffect(() => {
    if (tab?.documentTitle) {
      document.title = tab.documentTitle
    }
  }, [tab?.documentTitle])

  useEffect(() => {
    if (!eventId || isOtherSession) return undefined
    openSessionDetail({
      sessionId: eventId,
      ...toSessionCityPayload(city),
      returnTo: listingPath,
      returnLabel: "Returning to Shadow Working Events...",
    })
    return undefined
  }, [city, eventId, isOtherSession, listingPath, openSessionDetail])

  if (isOtherSession) {
    return (
      <EmbeddedCityShadowEventDetail
        sessionId={eventId}
        city={city}
        returnTo={listingPath}
      />
    )
  }

  return <Navigate to={listingPath || "/city-hub"} replace />
}

export default CityHubShadowEventDetailTab
